/**
 * Sign out: tell the server (session + presence ring in the admin), clear this browser's session exactly the way
 * today's site does (the same localStorage keys), then show the signed-out page.
 */
import type { NavigateFunction } from 'react-router-dom'
import { auth } from '~/api/endpoints'
import { clearAccessLevelCache } from '@/hooks/useAccessLevel'

const KEYS = ['auth_token', 'user_data', 'needsWelcome', 'token_refresh_lock', 'applicant_token', 'applicant_data', 'pending_invite_token', 'has_pending_subscription']

export async function signOutEverywhere(navigate: NavigateFunction) {
  // tell the server, but never wait more than 1.5 s (a hanging call made "Sign out" look broken)
  await Promise.race([Promise.allSettled([auth.presenceLeave(), auth.signOut()]), new Promise((r) => setTimeout(r, 1500))])
  try { KEYS.forEach((k) => localStorage.removeItem(k)) } catch { /* ignore */ }
  try { localStorage.removeItem('cw-pin-unlocked'); sessionStorage.removeItem('cw-pin-unlocked') } catch { /* ignore */ }
  clearAccessLevelCache()
  const at = new Date().toISOString()
  // a full reload so the auth state (AuthContext) starts clean; the signed-out page reads the time from the URL
  window.location.assign(`/signed-out?at=${encodeURIComponent(at)}`)
  void navigate
}

import { useRef } from 'react'
import { useAuth } from '@/features/auth'
/**
 * true once the first session check has finished. AuthContext's isLoading is also true while a code is being sent or
 * checked; using it to swap the whole page out would reset the enter page mid-login (today's site avoids this by
 * keeping the email above that switch). Only the very first check should show the waiting screen.
 */
export function useAuthReady() {
  const { isLoading } = useAuth()
  const ready = useRef(false)
  if (!isLoading) ready.current = true
  return ready.current
}
